/**
 * RemisesStatsPanel.js — V37.17
 *
 * Section « Demandes remises » pour le Super Admin dashboard.
 * Affiche :
 *   - 4 KPI Cards (total, délai moyen, pénalités 30j, quota par cooper)
 *   - Top 5 Coopers qui remettent le plus
 *   - Motifs de remise (camembert)
 *   - Distribution des délais (≤7j, 8-14j, 15-30j, >30j)
 *   - Courbe d'activité sur 30 jours
 *
 * Endpoint backend : GET /api/achats/admin/remises-stats
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import {
  PieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  Recycle, Clock, Coins, Users, Loader2, AlertCircle, TrendingUp,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Palette du camembert (motifs) — couleurs distinctes lisibles
const PIE_COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#ef4444', '#84cc16', '#a855f7', '#f97316'];

const formatShortDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
};

export default function RemisesStatsPanel({ getAuthHeader, refreshKey }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/achats/admin/remises-stats`, {
          headers: getAuthHeader ? getAuthHeader() : {},
        });
        if (!cancelled) setStats(res.data);
      } catch {
        if (!cancelled) setError('Impossible de charger les statistiques des remises.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getAuthHeader, refreshKey]);

  if (loading) {
    return (
      <Card className="border-slate-200" data-testid="remises-stats-loading">
        <CardContent className="py-10 flex items-center justify-center text-slate-500 gap-3">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Chargement des statistiques de remise…</span>
        </CardContent>
      </Card>
    );
  }

  if (error || !stats) {
    return (
      <Card className="border-red-200 bg-red-50/50" data-testid="remises-stats-error">
        <CardContent className="py-6 flex items-center gap-3 text-red-700">
          <AlertCircle className="h-5 w-5" />
          <span className="text-sm">{error || 'Données indisponibles.'}</span>
        </CardContent>
      </Card>
    );
  }

  const distData = Object.entries(stats.delai_distribution || {}).map(([k, v]) => ({ bucket: k, count: v }));
  const maxDist = Math.max(1, ...distData.map(d => d.count));
  const last7 = (stats.daily_30d || []).slice(-7).reduce((s, d) => s + d.count, 0);

  return (
    <div className="space-y-4" data-testid="remises-stats-panel">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <div className="p-2 rounded-lg bg-amber-100 text-amber-700">
          <Recycle className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: '"Outfit", system-ui, sans-serif' }}>
            Demandes remises en Communityplace
          </h2>
          <p className="text-xs text-slate-500">
            Surveiller la qualité du matching et détecter les abus
          </p>
        </div>
      </div>

      {/* 4 KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-slate-200" data-testid="remises-kpi-total">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 rounded-lg bg-indigo-100 text-indigo-600">
                <Recycle className="h-5 w-5" />
              </div>
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Total</span>
            </div>
            <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: '"Outfit", system-ui, sans-serif' }}>
              {stats.total}
            </div>
            <p className="text-xs text-slate-500 mt-1">Remises au total</p>
            <p className="text-[11px] text-slate-400 mt-2">{last7} ces 7 derniers jours</p>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="remises-kpi-delai-moyen">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 rounded-lg bg-emerald-100 text-emerald-600">
                <Clock className="h-5 w-5" />
              </div>
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Jours</span>
            </div>
            <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: '"Outfit", system-ui, sans-serif' }}>
              {stats.delai_moyen_jours}
            </div>
            <p className="text-xs text-slate-500 mt-1">Délai moyen avant remise</p>
            <p className="text-[11px] text-slate-400 mt-2">
              Tolérance gratuite : <span className="font-medium text-slate-600">{stats.grace_days}j</span>
            </p>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="remises-kpi-penalty">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 rounded-lg bg-rose-100 text-rose-600">
                <Coins className="h-5 w-5" />
              </div>
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Crédits</span>
            </div>
            <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: '"Outfit", system-ui, sans-serif' }}>
              {stats.penalty_total_30d}
            </div>
            <p className="text-xs text-slate-500 mt-1">Pénalités collectées · 30j</p>
            <p className="text-[11px] text-slate-400 mt-2">
              {stats.penalty_per_day} crédits/jour au-delà de J+{stats.grace_days}
            </p>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="remises-kpi-quota">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 rounded-lg bg-purple-100 text-purple-600">
                <TrendingUp className="h-5 w-5" />
              </div>
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Par cooper</span>
            </div>
            <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: '"Outfit", system-ui, sans-serif' }}>
              {stats.max_per_cooper_30d}
            </div>
            <p className="text-xs text-slate-500 mt-1">Quota max sur 30 jours</p>
            <p className="text-[11px] text-slate-400 mt-2">Anti-abus appliqué automatiquement</p>
          </CardContent>
        </Card>
      </div>

      {/* Top Coopers + Motifs Pie + Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Top 5 Coopers */}
        <Card className="border-slate-200" data-testid="remises-top-coopers">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Users className="h-4 w-4 text-indigo-600" />
              <h3 className="text-xs font-semibold text-slate-800 uppercase tracking-wider">
                Top 5 COOPERS · remettent le plus
              </h3>
            </div>
            <div className="space-y-2.5">
              {(stats.top_coopers || []).map((c, idx) => {
                const max = Math.max(1, ...(stats.top_coopers || []).map(x => x.count));
                const pct = Math.round((c.count / max) * 100);
                return (
                  <div key={c.label + idx} className="flex items-center gap-2">
                    <span className="text-[11px] text-slate-400 font-mono w-5">#{idx + 1}</span>
                    <span className="text-xs font-medium text-slate-700 flex-1 truncate" title={c.label}>{c.label}</span>
                    <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs text-slate-600 font-mono w-6 text-right">{c.count}</span>
                  </div>
                );
              })}
              {(!stats.top_coopers || stats.top_coopers.length === 0) && (
                <p className="text-xs text-slate-400 italic py-3 text-center">Aucune remise enregistrée</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Motifs Camembert */}
        <Card className="border-slate-200" data-testid="remises-motifs-pie">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <h3 className="text-xs font-semibold text-slate-800 uppercase tracking-wider">
                Motifs de remise
              </h3>
            </div>
            <div style={{ width: '100%', height: 180 }}>
              {(stats.motifs && stats.motifs.length > 0) ? (
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={stats.motifs}
                      dataKey="count"
                      nameKey="label"
                      cx="50%"
                      cy="50%"
                      outerRadius={60}
                      innerRadius={28}
                      paddingAngle={2}
                    >
                      {stats.motifs.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        fontSize: 12,
                        borderRadius: 8,
                        border: '1px solid #e2e8f0',
                      }}
                      formatter={(v, n) => [`${v} remise${v > 1 ? 's' : ''}`, n]}
                    />
                    <Legend
                      verticalAlign="bottom"
                      iconSize={8}
                      wrapperStyle={{ fontSize: 10, paddingTop: 4 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-xs text-slate-400 italic">Aucune donnée</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Distribution délais */}
        <Card className="border-slate-200" data-testid="remises-delai-distribution">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="h-4 w-4 text-emerald-600" />
              <h3 className="text-xs font-semibold text-slate-800 uppercase tracking-wider">
                Distribution des délais
              </h3>
            </div>
            <div className="space-y-3">
              {distData.map(d => {
                const pct = Math.round((d.count / maxDist) * 100);
                const colors = {
                  '<=7j': 'bg-emerald-500',
                  '8-14j': 'bg-amber-400',
                  '15-30j': 'bg-orange-500',
                  '>30j': 'bg-red-500',
                };
                return (
                  <div key={d.bucket}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-slate-700 font-medium">{d.bucket}</span>
                      <span className="text-xs text-slate-500 font-mono">{d.count}</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${colors[d.bucket] || 'bg-slate-400'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-400 mt-3 leading-relaxed">
              Tolérance gratuite : ≤ {stats.grace_days}j. Au-delà, {stats.penalty_per_day} crédits/jour débités.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Courbe 30 jours */}
      <Card className="border-slate-200" data-testid="remises-chart-30d">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider">
                Pipeline · 30 derniers jours
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Surveiller les pics de remise (signal qualité)
              </p>
            </div>
          </div>
          <div style={{ width: '100%', height: 200 }}>
            <ResponsiveContainer>
              <AreaChart
                data={(stats.daily_30d || []).map(d => ({ ...d, label: formatShortDate(d.date) }))}
                margin={{ top: 8, right: 12, bottom: 0, left: -20 }}
              >
                <defs>
                  <linearGradient id="remises-grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} minTickGap={20} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} width={28} />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                  formatter={(v) => [`${v} remise${v > 1 ? 's' : ''}`, 'Total']}
                />
                <Area type="monotone" dataKey="count" stroke="#f59e0b" strokeWidth={2} fill="url(#remises-grad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
